import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickActions = ({ 
  user = null, 
  userProfile = null,
  isPremium = false,
  currentPlan = null,
  hasUpcomingExercises = false,
  onGenerateWeeklyCheckin,
  className = ""
}) => {
  const quickActionItems = [
    {
      id: 'current-exercises',
      title: 'Current Exercises',
      description: 'Continue your weekly tasks',
      icon: 'Play',
      color: 'primary',
      path: '/exercise-management',
      enabled: hasUpcomingExercises,
      disabledReason: 'No exercises'
    },
    {
      id: 'view-plan',
      title: 'View Career Plan',
      description: 'Review your development roadmap',
      icon: 'FileText',
      color: 'secondary',
      path: '/ai-plan-generation',
      enabled: !!currentPlan,
      disabledReason: 'No plan'
    },
    {
      id: 'weekly-checkin',
      title: 'Weekly Check-in',
      description: 'Generate progress summary',
      icon: 'Calendar',
      color: 'success',
      action: onGenerateWeeklyCheckin,
      enabled: !!currentPlan,
      disabledReason: 'No plan',
      premium: true
    },
    {
      id: 'new-assessment',
      title: 'New Assessment',
      description: 'Start fresh career evaluation',
      icon: 'ClipboardList',
      color: 'warning',
      path: '/career-assessment-setup',
      enabled: true
    }
  ];

  const getColorClasses = (color, enabled) => {
    const baseColors = {
      primary: enabled ? "text-primary bg-primary/10 hover:bg-primary/20" : "text-muted-foreground bg-muted",
      secondary: enabled ? "text-secondary bg-secondary/10 hover:bg-secondary/20" : "text-muted-foreground bg-muted",
      success: enabled ? "text-success bg-success/10 hover:bg-success/20" : "text-muted-foreground bg-muted",
      warning: enabled ? "text-warning bg-warning/10 hover:bg-warning/20" : "text-muted-foreground bg-muted",
      error: enabled ? "text-error bg-error/10 hover:bg-error/20" : "text-muted-foreground bg-muted"
    };
    return baseColors?.[color] || baseColors?.primary;
  };

  const ActionButton = ({ item }) => {
    const isEnabled = item?.enabled && (!item?.premium || isPremium);
    
    const buttonContent = (
      <div className={`
        relative flex items-center space-x-3 p-3 rounded-lg border transition-all duration-200 cursor-pointer
        ${isEnabled 
          ? 'bg-card border-border hover:bg-muted/50 hover:shadow-sm' 
          : 'bg-muted border-muted text-muted-foreground cursor-not-allowed'
        }
      `}>
        <div className={`
          w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
          ${isEnabled ? 'bg-primary/10 text-primary' : 'bg-muted-foreground/10 text-muted-foreground'}
        `}>
          <Icon 
            name={item?.icon} 
            size={16} 
            className={isEnabled ? 'text-primary' : 'text-muted-foreground'} 
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-sm truncate ${isEnabled ? 'text-foreground' : 'text-muted-foreground'}`}>
            {item?.title}
          </h4>
          <p className={`text-xs truncate ${isEnabled ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
            {isEnabled ? item?.description : item?.disabledReason}
          </p>
        </div>

        {item?.premium && !isPremium && (
          <div className="flex-shrink-0">
            <Icon name="Crown" size={14} className="text-warning" />
          </div>
        )}
      </div>
    );

    if (!isEnabled) {
      return buttonContent;
    }

    if (item?.path) {
      return (
        <Link to={item?.path} className="block">
          {buttonContent}
        </Link>
      );
    }

    if (item?.action) {
      return (
        <button onClick={item?.action} className="block w-full">
          {buttonContent}
        </button>
      );
    }

    return buttonContent;
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Quick Actions</h3>
        <Icon name="Zap" size={20} className="text-primary" />
      </div>
      <div className="space-y-2">
        {quickActionItems?.map((item) => (
          <ActionButton key={item?.id} item={item} />
        ))}
      </div>
      {/* Additional Actions */}
      <div className="border-t border-border pt-4 space-y-3">
        <h4 className="text-sm font-semibold text-foreground">More Actions</h4>
        
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            disabled={!isPremium}
            className="text-xs justify-start w-full"
          >
            Export Progress
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            iconName="Share"
            iconPosition="left"
            className="text-xs justify-start w-full"
          >
            Share Achievement
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            iconName="Settings"
            iconPosition="left"
            className="text-xs justify-start w-full"
          >
            Preferences
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            iconName="HelpCircle"
            iconPosition="left"
            className="text-xs justify-start w-full"
          >
            Get Help
          </Button>
        </div>
      </div>
      {/* Premium Upgrade Prompt */}
      {!isPremium && (
        <div className="bg-gradient-to-r from-warning/10 to-primary/10 border border-warning/20 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="Crown" size={20} className="text-warning" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-foreground">Unlock Premium Actions</h4>
              <p className="text-xs text-muted-foreground">
                Access weekly check-ins, progress exports, and advanced features
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Crown"
              iconPosition="left"
              asChild
            >
              <Link to="/subscription-management">Upgrade</Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickActions;